import { Routes } from '@angular/router';
import { AuthGuard } from './core/guards/auth.guard';
import { SessionGuard } from './core/guards/session.guard';
import { OnboardingDataGuard } from './core/guards/onboarding-data.guard';
import { USER_TYPE } from './core/enums/user';
import { RoleGuard } from './core/guards/role.guard';

export const routes: Routes = [
  {
    path: 'auth',
    children: [
      {
        path: 'sign-in',
        loadComponent: () =>
          import('./auth/components/sign-in/sign-in.component').then((c) => c.SignInComponent),
        canActivate: [SessionGuard],
      },

      {
        path: 'register',
        loadComponent: () =>
          import('./auth/components/register/sign-up.component').then((c) => c.SignUpComponent),
        canActivate: [SessionGuard],
      },

      {
        path: 'sign-up',
        loadComponent: () =>
          import('./auth/components/sign-up/sign-up.component').then((c) => c.SignUpComponent),
        canActivate: [SessionGuard],
      },
      {
        canActivate: [SessionGuard],
        path: 'verify',
        loadComponent: () =>
          import('./auth/components/email-verification/email-verification.component').then(
            (c) => c.EmailVerificationComponent,
          ),
      },
      {
        path: 'forget-password',
        loadComponent: () =>
          import('./auth/components/forgot-password/forgot-password.component').then(
            (c) => c.ForgotPasswordComponent,
          ),
      },
      {
        path: 'reset-password',
        loadComponent: () =>
          import('./auth/components/reset-password/reset-password.component').then(
            (c) => c.ResetPasswordComponent,
          ),
      },
      {
        path: 'oauth-redirect',
        loadComponent: () =>
          import('./auth/components/oauth2-redirect/oauth2-redirect.component').then(
            (c) => c.OAuth2RedirectComponent,
          ),
      },
    ],
  },
  {
    path: 'tenant',
    loadComponent: () => import('./projects/tenants/tenants').then((m) => m.Tenants),
    // children: [{}]
  },
  {
    path: 'about-us',
    loadComponent: () => import('./home-pages/about-us/about-us').then((m) => m.AboutUs),
  },

  {
    path: 'features',
    loadComponent: () => import('./home-pages/features/features').then((m) => m.Features),
  },

  {
    path: 'terms-of-service',
    loadComponent: () =>
      import('./home-pages/terms-of-service/terms-of-service').then((m) => m.TermsOfService),
  },

  {
    path: 'contact-us',
    loadComponent: () => import('./home-pages/contact-us/contact-us').then((m) => m.ContactUs),
  },

  {
    path: 'privacy-policy',
    loadComponent: () =>
      import('./home-pages/privacy-policy/privacy-policy').then((m) => m.PrivacyPolicy),
  },

  {
    path: 'clients',
    loadComponent: () => import('./projects/clients/clients').then((m) => m.Clients),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      roles: [USER_TYPE.CLIENTS],
    },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./projects/clients/modules/layout/dashboard-layout').then(
            (m) => m.DashboardLayout,
          ),
        data: {
          breadcrumb: 'Dashboard',
          breadcrumbIcon: 'fa-solid fa-home',
          breadcrumbUrl: '/clients/dashboard',
        },
        children: [
          {
            path: '',
            redirectTo: 'dashboard',
            pathMatch: 'full',
          },
          {
            path: 'dashboard',
            loadComponent: () =>
              import('./projects/clients/modules/dashboard/dashboard').then((m) => m.Dashboard),
            data: { hideBreadcrumb: true },
          },
          {
            path: 'upload',
            loadComponent: () =>
              import('./projects/clients/modules/upload/upload').then((m) => m.Upload),
            data: { breadcrumb: 'Upload' },
          },
          {
            path: 'status',
            loadComponent: () =>
              import('./projects/clients/modules/status/status').then((m) => m.Status),
            data: { breadcrumb: 'Status' },
          },
          {
            path: 'tin-application',
            loadComponent: () => import('./projects/clients/modules/tin/tin').then((m) => m.Tin),
            data: { breadcrumb: 'TIN Application' },
          },
          {
            path: 'payment/services',
            loadComponent: () =>
              import('./projects/clients/modules/payment/service-selection/service-selection').then(
                (m) => m.ServiceSelection,
              ),
            data: { breadcrumb: 'Service Selection' },
          },
          {
            path: 'vat-filing',
            loadComponent: () =>
              import(
                './projects/clients/modules/payment/service-selection/components/client-vat-form/client-vat-form.component'
              ).then((m) => m.ClientVatFormComponent),
            data: { breadcrumb: 'VAT Filing' },
          },
          {
            path: 'wht-filing',
            loadComponent: () =>
              import(
                './projects/clients/modules/payment/service-selection/components/withholding-tax-form/withholding-tax-form.component'
              ).then((m) => m.WithholdingTaxFormComponent),
            data: { breadcrumb: 'WHT Filing' },
          },
          {
            path: 'tax-clearance',
            data: { breadcrumb: 'Tax Clearance Certificate' },
            children: [
              {
                path: '',
                loadComponent: () =>
                  import(
                    './projects/clients/modules/payment/service-selection/components/tax-clearance-redirect/tax-clearance-redirect.component'
                  ).then((m) => m.TaxClearanceRedirectComponent),
              },
              {
                path: 'company',
                loadComponent: () =>
                  import(
                    './projects/clients/modules/payment/service-selection/components/company-tax-clearance-cert-form/company-tax-clearance-cert-form.component'
                  ).then((m) => m.CompanyTaxClearanceCertFormComponent),
              },
              {
                path: 'individual',
                loadComponent: () =>
                  import(
                    './projects/clients/modules/payment/service-selection/components/tax-clearance-form/tax-clearance-form.component'
                  ).then((m) => m.TaxClearanceFormComponent),
              },
            ],
          },
          {
            path: 'corporate-filing',
            loadComponent: () =>
              import(
                './projects/clients/modules/payment/service-selection/components/corporate-filing-form/corporate-filing-form.component'
              ).then((m) => m.CorporateFilingFormComponent),
            data: { breadcrumb: 'Corporate Filing' },
          },
          {
            path: 'individual-tax-filing',
            loadComponent: () =>
              import(
                './projects/clients/modules/payment/service-selection/components/individual-tax-form/individual-tax-form.component'
              ).then((m) => m.IndividualTaxFormComponent),
            data: { breadcrumb: 'Individual Tax Filing' },
          },
          {
            path: 'payment-details',
            loadComponent: () =>
              import('./projects/clients/modules/payment/payment-details/payment-details').then(
                (m) => m.PaymentDetails,
              ),
            data: { breadcrumb: 'Tax Filings' },
          },
          {
            path: 'tax-payment-method',
            loadComponent: () =>
              import(
                './projects/clients/modules/payment/tax-payment-method/tax-payment-method'
              ).then((m) => m.TaxPaymentMethod),
            data: { hideBreadcrumb: true },
          },
          {
            path: 'payment-method',
            loadComponent: () =>
              import('./projects/clients/modules/payment-method/payment-method').then(
                (m) => m.PaymentMethod,
              ),
          },
          {
            path: 'tax-payment/callback',
            loadComponent: () =>
              import(
                './projects/clients/modules/payment/tax-payment-verification/tax-payment-verification'
              ).then((m) => m.TaxPaymentVerification),
            data: { breadcrumb: 'Payment Verification' },
          },
          {
            path: 'payment/callback',
            loadComponent: () =>
              import('./projects/clients/modules/payment-verifications/payment-verifications').then(
                (m) => m.PaymentVerifications,
              ),
          },

          {
            path: 'profile',
            loadComponent: () =>
              import('./projects/clients/modules/profile/profile').then((m) => m.Profile),
            data: { breadcrumb: 'Profile' },
          },
        ],
      },
      {
        path: 'company-onboarding',
        loadComponent: () =>
          import('./projects/clients/modules/onboarding/company/company').then((m) => m.Company),
      },
      {
        path: 'individual-onboarding',
        loadComponent: () =>
          import('./projects/clients/modules/onboarding/individual/individual').then(
            (m) => m.Individual,
          ),
      },
      {
        path: 'onboarding-success',
        loadComponent: () =>
          import(
            './projects/clients/modules/onboarding/onboarding-success/onboarding-success'
          ).then((m) => m.OnboardingSuccess),
      },
      {
        path: 'tax-assessment',
        canActivate: [OnboardingDataGuard],
        loadComponent: () =>
          import('./projects/clients/modules/onboarding/tax-assessment/tax-assessment').then(
            (m) => m.TaxAssessment,
          ),
      },
      {
        path: 'profile',
        loadComponent: () =>
          import('./projects/clients/modules/profile/profile').then((m) => m.Profile),
      },
      {
        path: 'service-selection',
        loadComponent: () =>
          import('./projects/clients/modules/service-selection/service-selection').then(
            (m) => m.ServiceSelection,
          ),
        data: { breadcrumb: 'Service Selection', breadcrumbIcon: 'fa-solid fa-list-check' },
      },
      {
        path: 'order-summary',
        loadComponent: () =>
          import('./projects/clients/modules/order-summary/order-summary').then(
            (m) => m.OrderSummary,
          ),
        data: { breadcrumb: 'Order Summary', breadcrumbIcon: 'fa-solid fa-file-invoice' },
      },
      {
        path: '',
        redirectTo: 'individual-onboarding',
        pathMatch: 'full',
      },
    ],
  },
  {
    path: 'tax-managers',
    canActivate: [AuthGuard],
    data: {
      roles: [USER_TYPE.TAX_PREPARER, USER_TYPE.ACCOUNTANT],
    },
    loadComponent: () => import('./projects/tax-managers/tax-managers').then((m) => m.TaxManagers),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./projects/tax-managers/modules/layout/dashboard-layout').then(
            (m) => m.DashboardLayout,
          ),
        children: [
          {
            path: '',
            redirectTo: 'dashboard',
            pathMatch: 'full',
          },
          {
            path: 'dashboard',
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./projects/tax-managers/modules/dashboard/dashboard').then(
                    (m) => m.Dashboard,
                  ),
                data: { hideBreadcrumb: true },
              },
              {
                path: 'supervisor',
                loadComponent: () =>
                  import(
                    './projects/tax-managers/modules/dashboard/components/supervisor/supervisor'
                  ).then((m) => m.Supervisor),
              },
              {
                path: 'bank-transfer-review',
                loadComponent: () =>
                  import(
                    './projects/tax-managers/modules/dashboard/components/bank-transfer-review/bank-transfer-review'
                  ).then((m) => m.BankTransferReview),
                data: { breadcrumb: 'Bank Transfer Review' },
              },
              {
                path: 'accountant',
                loadComponent: () =>
                  import(
                    './projects/tax-managers/modules/dashboard/components/accountant-dashboard/accountant-dashboard'
                  ).then((m) => m.AccountantDashboard),
                data: { breadcrumb: 'CA Review Dashboard' },
              },

              {
                path: 'cit',
                loadComponent: () =>
                  import(
                    './projects/tax-managers/modules/dashboard/components/company-income-tax/cit'
                  ).then((m) => m.CITComponent),
                data: { breadcrumb: 'Company Income Tax' },
              },

              {
                path: 'turnover-verification/:id',
                loadComponent: () =>
                  import(
                    './projects/tax-managers/modules/dashboard/components/turnover-verification/turnover-verification'
                  ).then((m) => m.TurnoverVerification),
                data: { breadcrumb: 'Turnover Verification' },
              },
            ],
          },
          {
            path: 'dashboard/firs-filing',
            loadComponent: () =>
              import(
                './projects/tax-managers/modules/dashboard/components/firs-filing-form/firs-filing-form.component'
              ).then((m) => m.FirsFilingFormComponent),
            data: { breadcrumb: 'FIRS Filing Form' },
          },
          {
            path: 'notifications',
            loadComponent: () =>
              import('./projects/tax-managers/modules/notifications/notifications').then(
                (m) => m.TaxManagerNotifications,
              ),
            data: { breadcrumb: 'Notifications' },
          },
          {
            path: 'referral',
            loadComponent: () =>
              import('./projects/tax-managers/modules/dashboard/components/referral/referral').then(
                (m) => m.Referral,
              ),
            data: { breadcrumb: 'Referral' },
          },
        ],
      },
    ],
  },
  {
    path: '',
    loadComponent: () =>
      import('./home-pages/landing-page/landing-page').then((m) => m.LandingPage),
  },
  {
    path: 'reset-password',
    redirectTo: 'auth/reset-password',
    pathMatch: 'full',
  },
  {
    path: 'payment/callback',
    redirectTo: '/clients/payment/callback',
    pathMatch: 'full',
  },
  {
    path: '**',
    redirectTo: 'auth/sign-in',
    pathMatch: 'full',
  },
];
